import React, { FC, ReactNode, useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { fetchTransmissions, configureMap, configurePlatform, configureStaticFiles } from 'store/global'
import Loader from 'components/Loader/Loader'
import { fetchCurrentUser, selectCurrentUser, selectAuthError } from 'store/global'
import ForbiddenAccessPage from 'uikit/pages/ForbiddenAccessPage/ForbiddenAccessPage'
import { handleLogout } from 'components/Header/utils'
import localStorageService from 'services/storage'

interface IAuthWrapper {
  children: ReactNode
}

const LOGIN_PATH = '/login'

// IVX-11142 — only fire the config/transmission wave when there is plausibly a
// session behind it.
//
// Every one of these routes is mounted `verifyToken, requirePlatformRole`, so
// without a session they can only 401. Firing them anyway cost five rejected
// requests per new browser session, which the platform audit stream recorded as
// five unattributable failures.
//
// Two gates, because they catch different cases:
//   - the login screen never has a session for a visitor who is actually
//     logging in, and AuthWrapper has no business driving API traffic there
//     (App.tsx renders it as a SIBLING of the /login route, so it mounts on the
//     login page too);
//   - a browser that has never signed in has no `hasSession` hint, so a
//     first-ever load fires currentUser alone.
//
// Neither gate can lose the wave: off the login screen, if currentUser resolves
// anyway, the effect below fires it; on the login screen it is deferred to the
// next page load, which is the only way off that route anyway (see the deferred
// effect). A returning signed-in browser still fires all five in
// parallel, which is the common case the IVX-9754 startup-waterfall work
// targeted — the perf win is kept, it is just no longer paid for by anonymous
// visitors. The hint is only a hint: the server gates these routes regardless,
// and a stale one costs at most the wave the app already fires today.
const hasPlausibleSession = (pathname: string) => pathname !== LOGIN_PATH && localStorageService.hasSession

const AuthWrapper: FC<IAuthWrapper> = ({ children }) => {
  const dispatch = useDispatch()
  const currentUser = useSelector(selectCurrentUser)
  const authError = useSelector(selectAuthError)
  const configWaveFired = useRef(false)

  // Read the route through the router rather than `window.location`. Captured
  // once: this decides what to do at boot, and must not re-run the mount effect
  // (and re-dispatch fetchCurrentUser) on every subsequent navigation.
  const { pathname } = useLocation()
  const bootPathname = useRef(pathname)

  const fireConfigWave = useCallback(() => {
    if (configWaveFired.current) return
    configWaveFired.current = true

    dispatch(fetchTransmissions())
    dispatch(configureMap())
    dispatch(configurePlatform())
    dispatch(configureStaticFiles())
  }, [dispatch])

  useEffect(() => {
    // fetchCurrentUser adopts the request primed in app.tsx before mount, so
    // this dispatch costs no extra round-trip and always runs — it is what
    // resolves the session in the first place.
    dispatch(fetchCurrentUser())

    // The config calls only need auth (not the currentUser body), so when a
    // session is plausible they run alongside it instead of waiting a full
    // round-trip for currentUser to resolve — shortening the startup waterfall
    // (IVX-9754).
    if (hasPlausibleSession(bootPathname.current)) fireConfigWave()
  }, [dispatch, fireConfigWave])

  const hasCurrentUser = Object.keys(currentUser).length !== 0

  useEffect(() => {
    if (!hasCurrentUser) return

    // currentUser resolved, so this browser really does have a working session.
    // Record the hint unconditionally — it describes the browser, not the route,
    // and it is what lets the next load fire the wave at mount.
    localStorageService.markSession()

    // Re-check the LIVE pathname (not bootPathname): AuthWrapper must not drive
    // API traffic from the login screen even when the session behind it is good.
    //
    // The only way to hold a live session on /login is the error card —
    // /login?error=access_denied — because Login.tsx redirects straight to the
    // auth provider otherwise. That is a dead end: leaving it means a full page
    // load, at which point markSession above has already been recorded, so the
    // mount gate fires the wave immediately. Config is deferred for this page
    // view, never lost.
    //
    // pathname is in the deps so this stays correct if /login ever becomes
    // reachable by client-side navigation; fireConfigWave is ref-guarded, so the
    // extra runs are idempotent.
    if (pathname === LOGIN_PATH) return

    fireConfigWave()
  }, [hasCurrentUser, pathname, fireConfigWave])

  useEffect(() => {
    if (authError?.type !== 'forbidden') return

    // IVX-11142 — currentUser came back 403: this user holds no platform role,
    // so every boot-wave route will deny them too. Without this, the hint
    // survives and each subsequent load fires the full wave for 5x403 — the
    // ticket's second burst, and the reason the fix cannot be scoped to 401s.
    //
    // Deliberately keyed off the currentUser gate's verdict rather than any 403
    // in the axios interceptor: a domain-level 403 ("you may not edit this
    // user") says nothing about whether the session itself is usable.
    localStorageService.clearSession()
  }, [authError])

  if (authError?.type === 'forbidden') {
    return (
      <ForbiddenAccessPage
        message={authError.message || 'You do not have permission to access this application.'}
        onSubmit={handleLogout}
      />
    )
  }

  if (!hasCurrentUser) {
    return <Loader type='globalLoading' message={''} />
  }

  return <>{children}</>
}

export default AuthWrapper
